import { DrawerIndentBackground } from "@base-ui/react";
import { ThemeProvider, createTheme, CssBaseline } from "@mui/material";

const muiTheme = createTheme({
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          backgroundColor: "var(--theme-surface-2)",
          "&.MuiButton-root": { color: "var(--theme-text)" },
        },
        outlined: { borderColor: "var(--theme-border)" },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: { color: "var(--theme-text)" },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          "& .MuiTableCell-root": {
            backgroundColor: "var(--theme-table-heading)",
            color: "var(--theme-table-heading-contrast)",
            borderColor: "var(--theme-table-heading-border)",
            fontWeight: 600,
          },
        },
      },
    },

    MuiTableBody: {
      styleOverrides: {
        root: {
          "& .MuiTableRow-root:nth-of-type(odd)": { backgroundColor: "var(--theme-table-row-odd)" },
          "& .MuiTableRow-root:nth-of-type(even)": { backgroundColor: "var(--theme-table-row-even)" },
          "& .MuiTableRow-root:hover": { backgroundColor: "var(--theme-table-row-hover)" },
          "& .MuiTableCell-root": {
            color: "var(--theme-table-row-contrast)",
            borderColor: "var(--theme-table-border)",
          },
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        icon: { color: "var(--theme-text-muted)" },
      },
    },

    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: "var(--theme-info-light)" },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          color: "var(--theme-text)",
          '&.Mui-disabled': {
            color: "var(--theme-text-muted)",
            WebkitTextFillColor: "var(--theme-text-muted)"
          }
        },
        notchedOutline: { borderColor: "var(--theme-border)" },
      },
    },

    MuiAutocomplete: {
      styleOverrides: {
        inputRoot: {
          backgroundColor: "var(--theme-surface-2)",
          color: "var(--theme-text)",
        },
        clearIndicator: {
          color: "var(--theme-text)",
        },
        popupIndicator: {
          color: "var(--theme-text)",
          '&.Mui-disabled': { color: "var(--theme-text-muted)" }
        },
        option: {
          color: "var(--theme-text)",
          backgroundColor: "var(--theme-surface-2)"
        },
        root: {
          width: "100%",
          "& .MuiFormLabel-root": { color: "var(--theme-text)" },
          "&:has(.MuiAutocomplete-tag)": { // Note: this is to target only multiselect boxes
            position: "relative",
            minHeight: "2.5rem",
            "& .MuiAutocomplete-inputRoot": {
              maxHeight: "2.5rem",
              overflowY: "scroll",
            },
          },
          "&:focus-within:has(.MuiAutocomplete-tag)": {
            zIndex: 40,
            "& .MuiAutocomplete-inputRoot": {
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              maxHeight: "16rem",
              overflowY: "auto",
              boxShadow: "0 8px 24px rgb(0 0 0 / 45%)",
              border: "1px solid var(--theme-border)",
            },
          },
          "&.Mui-focused .MuiFormLabel-root": {
            backgroundColor: "var(--theme-surface-2)",
            color: "var(--theme-text)"
          },
          "& .MuiChip-root": { backgroundColor: "var(--theme-surface) !important", transition: "none" },
          "& .MuiChip-deleteIcon": { color: "var(--theme-text) !important" },
        },
        listbox: {
          backgroundColor: "var(--theme-surface-2)",
          color: "var(--theme-text)",
          border: "1px solid var(--theme-table-border)",
          "& .MuiAutocomplete-option": { borderBottom: "1px solid var(--theme-border)" },
          "& .MuiAutocomplete-option.Mui-focused": { backgroundColor: "var(--theme-surface)" },
        }
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiInputLabel-root": { color: "var(--theme-text)" },
          backgroundColor: "var(--theme-surface-2)",
          borderColor: "var(--theme-border)",
          '& .Mui-disabled.MuiFormLabel-root': {
            color: "var(--theme-text-muted) !important"
          },
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: "var(--theme-surface-2)",
          color: "var(--theme-text)",
          borderColor: "var(--theme-border)",
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: { color: "var(--theme-text)" },
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: { color: "var(--theme-text)" },
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: { borderTop: "1px solid var(--theme-border)" },
      },
    },

    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          color: "var(--theme-text)",
          '&.Mui-disabled': { color: "var(--theme-text-muted)" },
        },
      },
    },

    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: "var(--theme-text-muted)",
          '&.Mui-disabled': { color: "var(--theme-border)" },
        },
      },
    },

    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiFormLabel-root': {
            color: "var(--theme-text)",
            borderColor: "var(--theme-border)",

          },
          '& .MuiPickersInputBase-sectionsContainer': {
            color: "var(--theme-text)",
            width: "65px"
          },
          '& .MuiPickersOutlinedInput-notchedOutline': {
            borderColor: "var(--theme-border)"
          },
          '& .MuiButtonBase-root': {
            color: "var(--theme-text)",
          },
        }
      }
    }
  },
});

export function AppMuiProvider({ children }: { children: React.ReactNode }) {
  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}